import React, { useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';
import { LADEN, Loading } from '../../administration/components';
import { EBENEN, EINSTELLUNGEN, sichtTitel } from '../../config/ebenen';
// Die zwei Ladetexte von USER (SPEC-USER.md 5.8, Ä-23): ein Wortlaut an einer Stelle. „Mein Zugang“ und „Nutzung“ zeigen
// dieselben Sätze, wenn sie ihre Daten laden; der Ersatz hier steht davor, solange die Seite selbst nachlädt
// (welle-2/UEBERGABE.md U1, US-G7; eingebunden im Schluss der Runde Welle 2).
import { LADE_TEXTE } from '../accessSummary';
import { MEIN_TEAM_TEXTE } from '../../administration/teams/teamTexte';
import { Bereich } from './Bereich';
import { bereichAusSlug, bereichsTitel, firmendatenAnsicht, seitenTitel } from './seiten';

// Plan 1a Welle 2 (SPEC-GEMEINSAM GB-01, Regel G-08; Ä-23, Ä-34): Laden ist nicht leer. Lädt eine Unterseite des Pop-ups
// nach, steht an ihrer Stelle schon ihr Seitenkopf mit dem richtigen Titel und darunter eine Zeile, die sagt, was lädt.
// Titel und Ladetext je Route von App.tsx stehen hier als reine Funktion, damit sie ohne App.tsx prüfbar sind
// (frontend/tests/laden-baustein.test.mjs).

/** Die Routen unter /einstellungen, die nachladen (App.tsx); „organisation“ ist organisation/:bereich?. */
export type LadeRoute = 'profil' | 'zugang' | 'nutzung' | 'nachrichten' | 'benachrichtigungen' | 'abrechnung' | 'tarife' | 'sicherheit' | 'praeferenzen' | 'darstellung' | 'daten'
  | 'mein-team' | 'sicht' | 'organisation/team-nutzung' | 'organisation/firmendaten' | 'organisation';
export type LadeAngaben = { titel: string; text: string };

/** Seitenkopf und Ladezeile des Ersatzes je Route. bereich: der Teil der Adresse hinter organisation/ (nur für
 *  „organisation“). Nie der Titel eines Bereichs, den niemand gewählt hat: ohne
 *  Bereich in der Adresse oder mit einem unbekannten heißt der Kopf wie die Ebene und die Zeile „Bereiche werden geladen …“.
 *  hash (nur für „organisation/firmendaten“; Plan 1a Welle 2, Spur admin, GZ-2): der Anker der Adresse – mit #besitz heißt der
 *  Kopf „Besitz und Nachfolge“, mit #sicherheit-der-firma „Sicherheit der Firma“ (seiten.ts firmendatenAnsicht). */
export function ladeErsatz(route: LadeRoute, angaben: { bereich?: string; hash?: string } = {}): LadeAngaben {
  switch (route) {
    case 'zugang': return { titel: seitenTitel('zugang'), text: LADE_TEXTE.zugang };
    case 'nutzung': return { titel: seitenTitel('nutzung'), text: LADE_TEXTE.nutzung };
    // Profil-Nachbau F-14 (PF-806): „Mein Team“ mit dem Ladesatz der Seite selbst.
    case 'mein-team': return { titel: seitenTitel('mein-team'), text: MEIN_TEAM_TEXTE.laden };
    case 'sicht': return { titel: sichtTitel(), text: LADEN.standard };
    case 'organisation/team-nutzung': return { titel: bereichsTitel('usage'), text: LADEN.standard };
    case 'organisation/firmendaten': return { titel: firmendatenAnsicht(angaben.hash ?? '').titel, text: LADEN.standard };
    case 'organisation': {
      const bereich = bereichAusSlug(angaben.bereich);
      return bereich ? { titel: bereichsTitel(bereich), text: LADEN.standard } : { titel: EBENEN.adminEinstellungen, text: EINSTELLUNGEN.bereicheLaden };
    }
    default: return { titel: seitenTitel(route), text: LADEN.standard };
  }
}

const UEBERSCHRIFT = '.einst-dialog .einst-inhalt .einst-kopf h1';
/** GZ-2: App.tsx nennt dem Ersatz der Route organisation/firmendaten den Titel ohne Anker („Firmendaten“). Mit #besitz oder
 *  #sicherheit-der-firma in der Adresse stünde beim Nachladen kurz der falsche Titel im Kopf; der Ersatz liest deshalb den
 *  Anker der echten Adresse (das Pop-up steht unter ihr, der Hintergrund hat seine eigene). Ohne Browser bleibt die Vorgabe. */
function titelDerAnsicht(vorgabe: string): string {
  if (typeof window === 'undefined' || vorgabe !== bereichsTitel('company')) return vorgabe;
  return ladeErsatz('organisation/firmendaten', { hash: window.location.hash }).titel;
}
/** Der Ersatz beim Nachladen einer Unterseite: der Seitenkopf des Ziels und darunter die Ladezeile.
 *  AE-03: Die Fokusregel des Rahmens (Einstellungen.tsx) setzt den Fokus nach dem Öffnen auf die sichtbare Überschrift; das
 *  ist während des Ladens die des Ersatzes. Verschwindet der Ersatz, ginge der Fokus mit ihm verloren. Lag er auf seiner
 *  Überschrift, geht er deshalb auf die Überschrift der geladenen Seite über. Der Abbau im Layout-Effekt läuft, solange der
 *  Ersatz noch im Dokument steht; nur dort ist zu sehen, wo der Fokus lag. Schlusslauf 5a (BEHEBEN 1): der erste Versuch
 *  läuft gleich nach dem Austausch (Mikroaufgabe), noch vor der nächsten Taste; erst wenn die Überschrift dann fehlt, in den
 *  folgenden Bildern. Vorher wartete schon der erste Versuch auf das nächste Bild: unter Last lag der Fokus so lange auf body,
 *  und ein Escape in dieser Zeit schloss das Pop-up nicht (AE-02). */
export function Ersatz({ titel: vorgabe, text }: { titel: string; text?: string }) {
  const titel = titelDerAnsicht(vorgabe);
  useLayoutEffect(() => () => {
    const aktiv = document.activeElement;
    if (!(aktiv instanceof HTMLElement) || !aktiv.matches(UEBERSCHRIFT)) return;
    const rahmen = aktiv.closest('.einst-dialog');
    let versuche = 0;
    const setzen = () => {
      // Liegt der Fokus inzwischen woanders (ein Klick, eine Taste), bleibt er dort.
      const jetzt = document.activeElement;
      if (jetzt && jetzt !== document.body && jetzt !== aktiv) return;
      const ziel = [...(rahmen?.querySelectorAll<HTMLElement>('.einst-inhalt .einst-kopf h1') ?? [])].find(el => el !== aktiv && el.getClientRects().length > 0);
      if (ziel) ziel.focus(); else if (versuche++ < 120) requestAnimationFrame(setzen);
    };
    queueMicrotask(setzen);
  }, []);
  return <Bereich titel={titel}><Loading text={text} /></Bereich>;
}
/** organisation/:bereich?: der Titel des Bereichs aus der Adresse, ohne Bereich der Name der Ebene. */
export function ErsatzOrganisation() {
  const { bereich } = useParams();
  return <Ersatz {...ladeErsatz('organisation', { bereich })} />;
}
